import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { WebsiteLayout } from '../../components/WebsiteLayout';
import { ComponentList } from '../../components/ComponentList';
import { useIsMimicking } from './useIsMimicking';
import { useWebsitePreviewData } from './useWebsitePreviewData';
import { LoadingState } from './LoadingState';
import { ErrorState } from './ErrorState';
import { PageNotFound } from './PageNotFound';
import { PreviewIndicator } from './PreviewIndicator';
import { LayoutComponents, processLayoutComponents } from './LayoutComponents';
import { PageContextProvider } from '../../contexts/PageContext';

export const WebsitePreview: React.FC = () => {
  const { siteId, slug } = useParams<{ siteId: string; slug?: string }>();
  const navigate = useNavigate();
  const isMimicking = useIsMimicking();
  const currentSlug = slug || '';

  const { data, refetch } = useWebsitePreviewData(siteId, currentSlug);

  const handleNavigation = (targetSlug: string) => {
    if (targetSlug === '' || targetSlug === 'home') {
      navigate(`/sites/${siteId}/preview`);
    } else {
      navigate(`/sites/${siteId}/preview/page/${targetSlug}`);
    }
  };

  // Loading state
  if (data.loading) {
    return <LoadingState isMimicking={isMimicking} />;
  }

  // Error state
  if (data.error || !data.website) {
    return <ErrorState isMimicking={isMimicking} error={data.error} onRetry={refetch} />;
  }

  // Page not found
  if (!data.page) {
    return (
      <PageNotFound
        isMimicking={isMimicking}
        website={data.website}
        navigation={data.navigation}
        theme={data.theme}
        siteId={siteId}
        currentSlug={currentSlug}
        onNavigateHome={() => handleNavigation('')}
      />
    );
  }

  // Process layout components
  const { headerComponents, footerComponents } = processLayoutComponents(data.layout?.components);

  // Render the full website in preview mode
  return (
    <PageContextProvider
      pageId={data.page.id}
      siteId={siteId ?? null}
      pageSlug={data.page.slug ?? ''}
    >
      <div className={`min-h-screen ${isMimicking ? 'mt-[52px]' : ''}`}>
        <PreviewIndicator />

        <WebsiteLayout
          website={data.website}
          page={data.page}
          navigation={data.navigation}
          theme={data.theme}
          isPreviewMode={true}
          previewSiteId={siteId}
          showBranding={data.showBranding}
        >
          {/* Top of page anchor for smooth scroll navigation */}
          <div id="top" />

          {/* Layout Header Components (before content-area) */}
          <LayoutComponents components={headerComponents} position="header" />

          {/* Page Components */}
          {data.components.length > 0 ? (
            <ComponentList
              components={data.components}
              websiteContext={{
                subdomain: data.website.subdomain || '',
                website: data.website,
                isPublicView: false,
              }}
            />
          ) : (
            <div className="min-h-96 flex items-center justify-center">
              <div className="text-center max-w-md mx-auto px-4">
                <h1 className="text-3xl font-bold text-gray-900 mb-4">{data.page.title}</h1>
                <p className="text-gray-600">
                  This page is empty. Add some content to get started!
                </p>
              </div>
            </div>
          )}

          {/* Layout Footer Components (after content-area) */}
          <LayoutComponents components={footerComponents} position="footer" />
        </WebsiteLayout>
      </div>
    </PageContextProvider>
  );
};

export default WebsitePreview;
